import { arrayMax, arrayMin, IDataLayer, IDataLayerType, IDataLayerZone, isArray, isNumber, range } from "../../omnia"
import { Class, SourceZone } from "./types"

import LayerValueType = omnia.models.LayerValueType

export function syncClasses(classes: Class[], index: number) {

    const t = classes[index]
    if (!t) return

    const p = classes[index - 1]
    if (p) {
        p.max = Math.max(t.min, p.min)
    }

    const n = classes[index + 1]
    if (n) {
        n.min = Math.min(t.max, n.max)
    }

}

export function isDiscreteType(type: IDataLayerType) {
    return type && (type.ValueType == LayerValueType.Discrete) && isArray(type.Values)
}
export function setDefaultDiscreteTargetValues(classes: Class[], type: IDataLayerType) {

    if (!classes.length) return classes

    const values = type.Values || []
    const l = values.length
    if (!l) return classes

    const step = values.length / classes.length

    return classes.map((c, index) => ({
        ...c,
        targetValue: values[Math.min((l - 1), Math.round(index * step))]
    }))

}

export function setDefaultTargetValue(classes: Class[], rounding: number): Class[] {

    return classes.map(z => ({
        ...z,
        targetValue: round((z.min + z.max) / 2, rounding)
    }))

}

export function getClassesByCount(limits?: { min: number, max: number }) {

    return (zones: SourceZone[], count: number, rounding: number) => {

        const { min, max } = limits
            ? roundLimits(limits, rounding)
            : getMinMax(zones, rounding)

        if (count <= 1) {
            return [{ min, max }]
        }

        const step = (max - min) / count

        const calc = (i: number) => round(min + (i * step), rounding)

        return range(count).map(i => ({
            min: i == 0 ? min : calc(i),
            max: i == (count - 1) ? max : calc(i + 1)
        }))

    }

}

export function getClassesByStep(limits?: { min: number, max: number }) {

    return (zones: SourceZone[], step: number, rounding: number) => {

        const { min, max } = limits
            ? roundLimits(limits, rounding)
            : getMinMax(zones, rounding)

        const mins = Math.ceil(min / step) * step
        const maxs = Math.floor(max / step) * step

        const count = Math.round((maxs - mins) / step) + ((mins > min) && (maxs < max) ? 1 : 0)

        const calc = (i: number) => round(mins + (i * step), rounding)

        return range(count).map(i => ({
            min: i == 0 ? min : calc(i),
            max: i == (count - 1) ? max : calc(i + 1)
        }))

    }

}


export function getLimits(points: IDataLayer["Points"]) {

    if (points) {
        let { Min: min, Max: max } = points
        min = min ?? 0
        max = max ?? 0
        if ((min || max) && (max >= min)) {
            return { min, max }
        }
    }
}

export function getSourceZones(layer: IDataLayer, getArea: (z: IDataLayerZone) => number): SourceZone[] {

    return layer.Zones
        .filter(z => isNumber(z.Value))
        .map(z => ({
            value: z.Value as number,
            area: z.Area || getArea(z),
            geometry: z.Geometry
        }))

}

function getMinMax(zones: SourceZone[], rounding: number) {
    const values = zones.map(z => z.value)
    const min = arrayMin(values, v => v)
    const max = arrayMax(values, v => v)
    return roundLimits({ min, max }, rounding)
}

function roundLimits({ min, max }: { min: number, max: number }, rounding: number) {

    return {
        min: Math.floor(min * rounding) / rounding,
        max: Math.ceil(max * rounding) / rounding,
    }

}

export function round(n: number, rounding: number) {
    return Math.round(n * rounding) / rounding
}

export function swapTargetValues(classes: Class[]) {

    const values = classes.map(c => c.targetValue).reverse()

    for (let index = 0; index < values.length; index++) {
        classes[index].targetValue = values[index]
    }

}
